import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Target, TrendingUp, Layers, AlertOctagon, Save } from 'lucide-react';

export default function ExecutiveAlignment() {
  const { user, token, isImpersonating } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  const [formData, setFormData] = useState({
    business_objectives: '',
    roi_targets: '',
    strategic_pillars: '',
    executive_blockers: ''
  });

  const fetchData = async () => {
    // 🚨 ARCHITECTURE RULE 1: Race Condition Guard
    if (!user || !user.org_id) return;

    try {
      setLoading(true);
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      
      // 🚨 ARCHITECTURE RULE 2: Clean Base Route
      const res = await axios.get(`${baseURL}/api/sprint/alignment`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data) {
        setFormData({
          business_objectives: res.data.business_objectives || '',
          roi_targets: res.data.roi_targets || '',
          strategic_pillars: res.data.strategic_pillars || '',
          executive_blockers: res.data.executive_blockers || ''
        });
      }
    } catch (error) {
      console.error("Failed to load alignment data", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // 🚨 ARCHITECTURE RULE 1: Dependency Array Inclusion
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.org_id, token]);

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      await axios.post(`${baseURL}/api/sprint/alignment`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ type: 'success', text: 'Executive Alignment successfully saved to strategy framework.' });
    } catch (error) {
      console.error("Save failed", error);
      setMessage({ type: 'error', text: 'Failed to save data. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 text-slate-500 text-center font-medium">Loading Executive Context...</div>;

  return (
    <div className="max-w-4xl mx-auto p-6 pb-24">
      

      <div className="flex justify-between items-end mb-8 border-b border-slate-200 pb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Day 1: Executive Alignment</h1>
          <p className="text-slate-500 mt-2">Correlate AI initiatives directly with 2026 operational targets.</p>
        </div>
      </div>

      {message.text && (
        <div className={`p-4 rounded-xl mb-6 text-sm font-bold ${message.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          {message.type === 'success' ? '✅ ' : '❌ '}{message.text}
        </div>
      )}

      <div className="space-y-6">
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
          <label className="flex items-center gap-2 text-lg font-bold text-slate-800 mb-2">
            <Target className="w-5 h-5 text-blue-600" /> Core 2026 Business Objectives
          </label>
          <p className="text-xs text-slate-500 mb-3">Define the primary business goals (e.g., "Reduce supply chain overhead by 15%", "Scale support without headcount").</p>
          <textarea 
            value={formData.business_objectives}
            onChange={(e) => setFormData({...formData, business_objectives: e.target.value})}
            className="w-full min-h-[120px] p-4 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm"
            placeholder="Enter objectives..."
          />
        </div>

        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
          <label className="flex items-center gap-2 text-lg font-bold text-slate-800 mb-2">
            <TrendingUp className="w-5 h-5 text-emerald-600" /> AI ROI & Financial Targets
          </label>
          <p className="text-xs text-slate-500 mb-3">What are the specific financial metrics or friction costs this sprint aims to optimize?</p>
          <textarea 
            value={formData.roi_targets}
            onChange={(e) => setFormData({...formData, roi_targets: e.target.value})}
            className="w-full min-h-[120px] p-4 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none text-sm"
            placeholder="Enter ROI targets..."
          />
        </div>

        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
          <label className="flex items-center gap-2 text-lg font-bold text-slate-800 mb-2">
            <Layers className="w-5 h-5 text-purple-600" /> Strategic AI Pillars
          </label>
          <p className="text-xs text-slate-500 mb-3">Which domains are we isolating for the workflow mapping (e.g., Engineering, HR, Customer Success)?</p>
          <textarea 
            value={formData.strategic_pillars}
            onChange={(e) => setFormData({...formData, strategic_pillars: e.target.value})}
            className="w-full min-h-[120px] p-4 border border-slate-300 rounded-xl focus:ring-2 focus:ring-purple-500 outline-none text-sm"
            placeholder="Enter strategic pillars..."
          />
        </div>

        <div className="bg-amber-50 p-6 rounded-2xl border border-amber-200 shadow-sm">
          <label className="flex items-center gap-2 text-lg font-bold text-amber-800 mb-2">
            <AlertOctagon className="w-5 h-5 text-amber-600" /> Executive Blockers / Concerns
          </label>
          <p className="text-xs text-amber-700 mb-3">Log any immediate objections raised by the C-Suite (e.g., Data sovereignty constraints, IP leakage fears).</p>
          <textarea 
            value={formData.executive_blockers}
            onChange={(e) => setFormData({...formData, executive_blockers: e.target.value})}
            className="w-full min-h-[100px] p-4 border border-amber-300 rounded-xl focus:ring-2 focus:ring-amber-500 outline-none text-sm bg-white"
            placeholder="Enter known blockers..."
          />
        </div>

        <div className="flex justify-end pt-4">
          <button 
            onClick={handleSave}
            disabled={saving || isImpersonating}
            className={`flex items-center gap-2 px-8 py-3 font-bold rounded-xl shadow-md transition-all ${
              isImpersonating 
                ? 'bg-slate-300 text-slate-500 cursor-not-allowed' 
                : 'bg-slate-900 text-white hover:bg-slate-800 hover:shadow-lg'
            }`}
          >
            <Save className="w-5 h-5" />
            {isImpersonating ? '🔒 Read-Only' : (saving ? 'Saving...' : 'Save Alignment Matrix')}
          </button>
        </div>
      </div>
    </div>
  );
}